import AddCircleIcon from "@mui/icons-material/AddCircle";
import { LoadingButton } from "@mui/lab";
import { Button, FormControlLabel, Grid, Stack, Switch, TextField, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";

import { EnhancedCenter } from "../../../../Models/Center";
import { EnhancedCenterDialTime } from "../../../../Models/CenterDialTime";
import ClearableTextField from "../../../Helpers/ClearableTextField";
import SelectList from "../../../Helpers/SelectList";
import AdminFormLabel from "../../AdminReadmeFormLabel";
import ToolWrapper from "../ToolWrapper";
import DialTimeForm from "./CenterSettings.DialTimeForm";
import CenterListItem from "./CenterSettings.ListItem";
import { dialTimesReadme, editingCenterReadme, toolReadme } from "./CenterSettings.Readme";
import useGetCenters from "./CenterSettings.UseGetCenters";
import useGetDialTimeMinMax from "./CenterSettings.UseGetDialTimeMinMax";
import usePutCenters from "./CenterSettings.UsePutCenter";

const CenterSettings: React.FC = () => {
  const [centers, setCenters] = useState<EnhancedCenter[]>();
  const [filter, setFilter] = useState("");
  const [selectedCenter, setSelectedCenter] = useState<EnhancedCenter>();

  const { res: getResponse, loading: getLoading } = useGetCenters();
  useEffect(() => getResponse && setCenters(getResponse.payload.map(c => new EnhancedCenter(c))), [getResponse]);
  const { response: getMinMaxResponse, isLoading: getMinMaxLoading } = useGetDialTimeMinMax();
  const { response: putResponse, isLoading: putLoading, setUpdate } = usePutCenters();
  useEffect(() => {
    if (!putResponse) return;
    if (putResponse.length && putResponse[0].isSuccess && putResponse[0].dto) {
      setCenters(oldCenters => {
        if (!oldCenters) throw "Centers not loaded.";
        const index = oldCenters.findIndex(c => c.id === putResponse[0].dto.id);
        if (index === -1) throw "Center to update could not be found.";
        oldCenters[index] = new EnhancedCenter(putResponse[0].dto);
        return [...oldCenters];
      });
      setSelectedCenter(new EnhancedCenter(putResponse[0].dto));
    }
  }, [putResponse]);

  const originalSelectedCenterValue = centers?.find(c => c.id === selectedCenter?.id);
  const isChangedAndValid =
    centers &&
    selectedCenter?.dialTimes.every(p => p.dialTime) &&
    originalSelectedCenterValue &&
    EnhancedCenter.isChanged(selectedCenter, originalSelectedCenterValue);

  const handleDialerCountChange = (newValue: string) =>
    setSelectedCenter(
      c => c && { ...c, dialerCount: isNaN(Number.parseInt(newValue)) ? undefined : Number.parseInt(newValue) }
    );
  const handleDialTimeDelete = (index: number) => () => {
    setSelectedCenter(c => {
      if (c) c.dialTimes.splice(index, 1);
      return c ? { ...c } : c;
    });
  };
  const handleDialTimeUpdate = (index: number) => (dialTimeUpdate: EnhancedCenterDialTime) => {
    setSelectedCenter(c => {
      if (!c) return c;
      c.dialTimes[index] = dialTimeUpdate;
      return { ...c };
    });
  };

  return (
    <ToolWrapper title="Center Settings" readme={toolReadme} isLoading={getLoading || getMinMaxLoading}>
      <Grid container columnSpacing={2} wrap="nowrap" sx={{ height: "100%", p: 2 }}>
        <Grid item container direction="column" rowSpacing={2} sx={{ height: "100%" }}>
          <Grid item>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Centers:</Typography>
              <ClearableTextField
                onClear={() => setFilter("")}
                value={filter}
                onChange={({ target: { value } }) => setFilter(value)}
                label="Filter..."
              />
            </Stack>
          </Grid>
          <Grid item xs sx={{ overflowY: "auto", overflowX: "hidden", p: 0.5 }}>
            <SelectList
              items={
                filter ? centers?.filter(c => c.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase())) : centers
              }
              isValueSelected={option => option.id === selectedCenter?.id}
              disabled={putLoading}
              onChange={centers => setSelectedCenter(centers[0] && new EnhancedCenter(centers[0]))}
              renderListItem={props => <CenterListItem {...props} />}
            />
          </Grid>
        </Grid>
        <Grid item container direction="column" rowSpacing={1}>
          <Grid item sx={{ minWidth: "18rem" }}>
            <AdminFormLabel
              label={`Editing${selectedCenter?.name ? ` ${selectedCenter.name}` : ""}:`}
              readme={editingCenterReadme}
            />
          </Grid>
          <Grid item container spacing={2} columns={2}>
            <Grid item sm={12} md={6}>
              <FormControlLabel
                control={<Switch />}
                disabled={!selectedCenter || putLoading}
                checked={selectedCenter?.isActive || false}
                onChange={() => setSelectedCenter(c => c && { ...c, isActive: !c.isActive })}
                label="GEM Enabled"
              />
            </Grid>
            <Grid item sm={12} md={6}>
              <FormControlLabel
                control={<Switch />}
                disabled={!selectedCenter || putLoading}
                checked={selectedCenter?.isObxDisabled ? false : true}
                onChange={() => setSelectedCenter(c => c && { ...c, isObxDisabled: !c.isObxDisabled })}
                label="OBX Enabled"
              />
            </Grid>
            <Grid item sm={12} md={6}>
              <TextField
                type="text"
                disabled={!selectedCenter || !selectedCenter.isActive || putLoading}
                value={selectedCenter?.dialerCount ?? ""}
                onChange={e => handleDialerCountChange(e.target.value)}
                label="Outbound Call Count"
              />
            </Grid>
            <Grid item sm={12} md={6}>
              <FormControlLabel
                control={<Switch />}
                disabled={!selectedCenter || !selectedCenter.isActive || putLoading}
                checked={selectedCenter?.isTextable || false}
                onChange={() => setSelectedCenter(c => c && { ...c, isTextable: !c.isTextable })}
                label="Is Textable"
              />
            </Grid>
          </Grid>
          <Grid item container justifyContent="center">
            <AdminFormLabel label="Dial Times" readme={dialTimesReadme} />
          </Grid>
          {selectedCenter?.dialTimes.map((dialTime, index) => (
            <DialTimeForm
              dialTime={dialTime}
              updateDialTime={handleDialTimeUpdate(index)}
              deleteDialTime={handleDialTimeDelete(index)}
              disabled={!selectedCenter?.isActive || putLoading}
              key={index}
            />
          ))}
          <Grid item container justifyContent="center">
            <Button
              startIcon={<AddCircleIcon />}
              onClick={() =>
                setSelectedCenter(
                  c =>
                    c && {
                      ...c,
                      dialTimes: getMinMaxResponse
                        ? c.dialTimes.concat({
                            ...new EnhancedCenterDialTime(),
                            dialTime: getMinMaxResponse.minimum.toISOTime({ includeOffset: false }),
                            dialTimeDateTime: getMinMaxResponse.minimum.plus({ minutes: 0 })
                          })
                        : c.dialTimes
                    }
                )
              }
              disabled={!selectedCenter || !selectedCenter.isActive || putLoading}
            >
              Add Dial Time
            </Button>
          </Grid>
          <Grid item container justifyContent="center">
            <LoadingButton
              variant="outlined"
              loading={putLoading}
              disabled={!isChangedAndValid || putLoading}
              onClick={() => setUpdate(u => (selectedCenter ? EnhancedCenter.toBase(selectedCenter) : u))}
            >
              Save Changes
            </LoadingButton>
          </Grid>
        </Grid>
      </Grid>
    </ToolWrapper>
  );
};

export default CenterSettings;
